import { apiIsomorphicFetchUserEnvironment } from '#/api/userEnvironment/apiFetchUserEnvironment';
import type { LayoutHandleProps } from '#/layouts/Layout/LayoutHandleProps';
import { idealistRequireAuthentication } from '#/modules/auth/routing/idealist-require-authentication';
import { orgDashboardApiIsomorphicFetchOrg } from '#/modules/orgDashboard/api/orgDashboardApiFetchOrg';
import { orgDashboardRouteGroup } from '#/modules/orgDashboard/routing/orgDashboard.routes';
import type { HorizonAppRouteGroupLoaderArgs } from '#/routing/classes/types/AppRouteGroupLoaderArgs';
import type { HorizonAppRouteComponentProps } from '#/routing/types/HorizonAppRouteComponentProps';
import type { HorizonAppRouteGroupShouldRevalidateFunctionArgs } from '#/routing/types/HorizonAppRouteGroupShouldRevalidateFunctionArgs';

import { OrgDashboardOrgSubLayoutLoadable } from './OrgDashboardOrgSubLayoutLoadable';

export function shouldRevalidate({
  currentParams,
  nextParams,
}: HorizonAppRouteGroupShouldRevalidateFunctionArgs<
  typeof orgDashboardRouteGroup
>) {
  return currentParams.orgId !== nextParams.orgId;
}

export const handle = {
  searchFromPageType: 'DASHBOARD',
} satisfies LayoutHandleProps;

export async function loader({
  request,
  context,
  params: { orgId },
}: HorizonAppRouteGroupLoaderArgs<typeof orgDashboardRouteGroup>) {
  const [{ user }, orgResponse] = await Promise.all([
    apiIsomorphicFetchUserEnvironment({
      request,
      context,
    })(),
    orgDashboardApiIsomorphicFetchOrg({ request, context })(orgId).catch(
      () => null,
    ),
    OrgDashboardOrgSubLayoutLoadable.load(),
  ]);

  if (!user || orgResponse === null)
    return idealistRequireAuthentication({ request });

  const { org } = orgResponse;

  const defaultSearchType = (() => {
    const { preferredListingTypes } = org;
    if (!preferredListingTypes) return 'JOB';

    if (preferredListingTypes.includes('JOB')) return 'JOB';
    if (preferredListingTypes.includes('VOLOP')) return 'VOLOP';
    if (preferredListingTypes.includes('INTERNSHIP')) return 'INTERNSHIP';

    if (
      preferredListingTypes.includes('NOT_SURE') ||
      preferredListingTypes.includes('NOT_HERE_TO_POST')
    )
      return 'JOB';
  })();

  return {
    handle: { defaultSearchType } satisfies LayoutHandleProps,
    org,
  };
}

export default function OrgDashboardOrgSubLayoutHorizonRoute({
  loaderData: { org },
}: HorizonAppRouteComponentProps<typeof loader>) {
  return <OrgDashboardOrgSubLayoutLoadable key={org.id} initialOrg={org} />;
}
